import { COLLECT_COPY } from './collectCopy'
import { EMAILS } from './communication'
import {
  ASK,
  CARD,
  COMPOSE,
  CONTESTS,
  GUIDES,
  HELP,
  HOME,
  NOTICES_PAGE,
  ONE_QUESTION,
  PROFILE,
  SHELL,
  SHOP,
  STARS,
  SUPPORT,
  TERMS,
  TOUR,
  WIKI,
} from './hubCopy'
import { LANGUAGE_HEADING } from './locale'
import { NOTICE_STATUS } from './notices'
import { STOCK_REASON_HEADING, stockRefusal } from './shop'
import { SECTIONS } from './shopAdmin'
import { SIGN_IN_COPY } from './signInCopy'
import { SIGN_IN_ERRORS, SUPPORT_ADDRESS } from './signInErrors'
import { DESK_VIEW_LABELS, PRIORITY_LABELS } from './support'
import { STAFF_LEVEL_LABELS } from './supportStars'

/**
 * THE APP'S OWN WORDS, QUOTABLE FROM A GUIDE (31 Oct, HLP-45; v2 S3).
 *
 * A guide that retypes a button's label drifts from the button, and nobody
 * notices until a manager follows it (REC-68's whole reason, 4 Oct). So a
 * guide file writes `{{SHOP.pickupDone}}` and this registry fills it from the
 * constant the screen itself renders - rewording the screen rewords the
 * guide. The suite fails on a key a guide uses that is not here, so a guide
 * can never print `{{...}}` at someone.
 *
 * English, like every admin screen the role guides describe (hubCopy.ts). A
 * key that takes an argument (`{{SHOP.opensOn|1 Feb 2027}}`) is a function of
 * one string; the argument is example text in the guide, never a person.
 *
 * This is the ONE registry. Add a key here when a guide needs to quote a new
 * screen; do not inline the words into the guide file.
 */
type Quote = string | ((arg: string) => string)

const shop = SHOP.en
const collect = COLLECT_COPY.en

export const GUIDE_COPY: Record<string, Quote> = {
  'SHOP.waiting': shop.waiting,
  'SHOP.readyToCollect': shop.readyToCollect,
  'SHOP.collected': shop.collected,
  'SHOP.cancelled': shop.cancelled,
  'SHOP.yourOrders': shop.yourOrders,
  'SHOP.pickupWithCode': shop.pickupWithCode,
  'SHOP.pickupDone': shop.pickupDone,
  'SHOP.lastsHead': shop.lastsHead,
  'SHOP.preordered': shop.preordered,
  'SHOP.preorderUntil': (d) => shop.preorderUntil(d),
  'SHOP.otherPart': shop.otherPart,
  'SHOP.opensOn': (d) => shop.opensOn(d),
  'SHOP.ordersClosed': shop.ordersClosed,
  'SHOP.oneEach': shop.oneEach,
  'COLLECT.forWhom': (who) => collect.forWhom(who),
  'COLLECT.confirm': collect.confirm,
  'COLLECT.unknownTitle': collect.unknownTitle,
  'COLLECT.notReadyTitle': collect.notReadyTitle,
  'COLLECT.expiredTitle': collect.expiredTitle,
  'STOCK_REASON.waiting': STOCK_REASON_HEADING.waiting,
  'STOCK_REASON.out': STOCK_REASON_HEADING.out,
  'STOCK_REASON.low': STOCK_REASON_HEADING.low,
  'STOCK_REASON.recent': STOCK_REASON_HEADING.recent,
  // The Stock tab's own refusal, for the example the guide gives (0 typed, 3
  // promised) - so the guide cannot quote a rule the screen does not enforce.
  'STOCK.refusal': stockRefusal(0, 3) ?? '',
  'SECTIONS.all': SECTIONS.map((s) => s.title).join(', '),
  ...Object.fromEntries(SECTIONS.map((s, i) => [`SECTIONS.${i}`, s.title])),
  // S5 (HLP-47): the words the four new guides quote, each the constant the
  // screen renders (the claim tables are in docs/build-log.md, "The guides,
  // written"). A key that names a nav item uses the shell's own label.
  'ASK.ask': ASK.en.ask,
  'ASK.attachPicture': ASK.en.attachPicture,
  'ASK.capturePage': ASK.en.capturePage,
  'ASK.helpLead': ASK.en.helpLead,
  'ASK.helpLink': ASK.en.helpLink,
  'ASK.inferred': ASK.en.inferred,
  'ASK.lookedAt': ASK.en.lookedAt,
  'ASK.removePicture': ASK.en.removePicture,
  'ASK.requestLink.idea': ASK.en.requestLink.idea,
  'ASK.requestLinkNote': ASK.en.requestLinkNote,
  'ASK.startersHead': ASK.en.startersHead,
  'ASK.startOver': ASK.en.startOver,
  'ASK.wasUseful': ASK.en.wasUseful,
  'ASK.whatWasWrong': ASK.en.whatWasWrong,
  'CARD.editCard': CARD.en.editCard,
  'CARD.howItWorks': CARD.en.howItWorks,
  'CARD.saveContact': CARD.en.saveContact,
  'CARD.saveImage': CARD.en.saveImage,
  'CARD.share': CARD.en.share,
  'CARD.show': CARD.en.show,
  'COMPOSE.addPhotos': COMPOSE.en.addPhotos,
  'COMPOSE.checkedNote': COMPOSE.en.checkedNote,
  'COMPOSE.optOutLink': COMPOSE.en.optOutLink,
  'COMPOSE.post': COMPOSE.en.post,
  'COMPOSE.postAsLabel': COMPOSE.en.postAsLabel,
  'COMPOSE.saveAsDraft': COMPOSE.en.saveAsDraft,
  'COMPOSE.takeDown': COMPOSE.en.takeDown,
  'CONTESTS.takeBack': CONTESTS.en.takeBack,
  'CONTESTS.voteFor': CONTESTS.en.voteFor,
  'DESK_VIEW.asker': DESK_VIEW_LABELS.asker,
  'DESK_VIEW.done': DESK_VIEW_LABELS.done,
  'DESK_VIEW.holder': DESK_VIEW_LABELS.holder,
  'EMAILS.head': EMAILS.en.head,
  'EMAILS.lede': EMAILS.en.lede,
  'GUIDES.askAbout': GUIDES.en.askAbout,
  'HELP.guidesOpen': HELP.en.guidesOpen,
  'HELP.kindAll': HELP.en.kindAll,
  'HELP.kindFeedback': HELP.en.kindFeedback,
  'HELP.kindQuestion': HELP.en.kindQuestion,
  'HELP.newAskOne': HELP.en.newAskOne,
  'HELP.newFeedback': HELP.en.newFeedback,
  'HELP.newLabel': HELP.en.newLabel,
  'HELP.newTicket': HELP.en.newTicket,
  // 348 (D4): the ticket's "how much is this in your way?" and its answers.
  'HELP.priorityLabel': HELP.en.priorityLabel,
  'HELP.priority.cant_work': HELP.en.priorities.cant_work.label,
  'HELP.priority.blocked': HELP.en.priorities.blocked.label,
  'HELP.priority.annoying': HELP.en.priorities.annoying.label,
  'HELP.priority.question': HELP.en.priorities.question.label,
  'PRIORITY.cant_work': PRIORITY_LABELS.cant_work,
  'PRIORITY.blocked': PRIORITY_LABELS.blocked,
  'HELP.sendFeedback': HELP.en.sendFeedback,
  'HELP.ticketsTitle': HELP.en.ticketsTitle,
  'HELP.whoReads': HELP.en.whoReads,
  'HELP.wikiBody': HELP.en.wikiBody,
  'HOME.editThisPost': HOME.en.editThisPost,
  'HOME.postedBy': (a) => HOME.en.postedBy(a),
  'HOME.postSomething': HOME.en.postSomething,
  'HOME.recognizeColleague': HOME.en.recognizeColleague,
  'HOME.showOriginal': HOME.en.showOriginal,
  'HOME.translatedByOne': HOME.en.translatedByOne,
  'HOME.videoChangeTime': HOME.en.videoChangeTime,
  'HOME.videoHiddenUntil': (a) => HOME.en.videoHiddenUntil(a),
  'HOME.videoHideUntil': HOME.en.videoHideUntil,
  'HOME.videoShowFrom': HOME.en.videoShowFrom,
  'HOME.videoShowNow': HOME.en.videoShowNow,
  'HOME.videoShownToAll': HOME.en.videoShownToAll,
  'HOME.videoViewsTitle': HOME.en.videoViewsTitle,
  'HOME.yourDrafts': HOME.en.yourDrafts,
  'LOCALE.heading': LANGUAGE_HEADING,
  'NOTICE_STATUS.ended': NOTICE_STATUS.ended.label,
  'NOTICE_STATUS.live': NOTICE_STATUS.live.label,
  'NOTICE_STATUS.pending': NOTICE_STATUS.pending.label,
  'NOTICE_STATUS.taken_down': NOTICE_STATUS.taken_down.label,
  'NOTICES_PAGE.manageHeading': NOTICES_PAGE.en.manageHeading,
  'NOTICES_PAGE.previewHeading': NOTICES_PAGE.en.previewHeading,
  'ONE_QUESTION.edit': ONE_QUESTION.en.edit,
  'ONE_QUESTION.submit': ONE_QUESTION.en.submit,
  'ONE_QUESTION.withdraw': ONE_QUESTION.en.withdraw,
  'PROFILE.languageHead': PROFILE.en.languageHead,
  'PROFILE.photoOnlyOnCard': PROFILE.en.photoOnlyOnCard,
  'PROFILE.readHead': PROFILE.en.readHead,
  'PROFILE.whatWeHold': PROFILE.en.whatWeHold,
  'SHELL.bellAll': SHELL.en.bellAll,
  'SHELL.bellNew': SHELL.en.bellNew,
  'SHELL.hubShort': SHELL.en.hubShort,
  'SHELL.markAllRead': SHELL.en.markAllRead,
  'SHELL.navAdmin': SHELL.en.nav.Admin,
  'SHELL.navHelp': SHELL.en.nav.Help,
  'SHELL.navPhotos': SHELL.en.nav.Photos,
  'SHELL.navSurveys': SHELL.en.nav.Surveys,
  'SHELL.signOut': SHELL.en.signOut,
  'SHELL.takeTour': SHELL.en.takeTour,
  'SHELL.terms': SHELL.en.terms,
  'SHELL.yourCard': SHELL.en.yourCard,
  'SHELL.yourProfile': SHELL.en.nav['Your profile'],
  'SIGNIN.emailMeACode': SIGN_IN_COPY.en.emailMeACode,
  'SIGNIN.inactiveTitle': SIGN_IN_COPY.en.inactiveTitle,
  'SIGNIN.notFoundEmail': SIGN_IN_ERRORS.en.notFoundEmail,
  'SIGNIN.sendAnother': SIGN_IN_COPY.en.sendAnother,
  'SIGNIN.supportAddress': SUPPORT_ADDRESS,
  'SIGNIN.usePhoneInstead': SIGN_IN_COPY.en.usePhoneInstead,
  'STAR_LEVEL.critical': STAFF_LEVEL_LABELS.critical,
  'STAR_LEVEL.important': STAFF_LEVEL_LABELS.important,
  'STAR_LEVEL.thanks': STAFF_LEVEL_LABELS.thanks,
  'STARS.addStars': STARS.en.addStars,
  'STARS.give': STARS.en.give,
  'STARS.inYourWords': STARS.en.inYourWords,
  'STARS.recognizeColleague': STARS.en.recognizeColleague,
  'STARS.store': STARS.en.store,
  'STARS.wall': STARS.en.wall,
  'STARS.whatFor': STARS.en.whatFor,
  'STARS.who': STARS.en.who,
  'STARS.withYourName': STARS.en.withYourName,
  'SUPPORT.addNote': SUPPORT.en.addNote,
  'SUPPORT.closedAsk': SUPPORT.en.closedAsk,
  'SUPPORT.closeThread': SUPPORT.en.closeThread,
  'SUPPORT.details': SUPPORT.en.details,
  'SUPPORT.didItAnswer': SUPPORT.en.didItAnswer,
  'SUPPORT.handedToYou': SUPPORT.en.handedToYou,
  'SUPPORT.insertSavedReply': SUPPORT.en.insertSavedReply,
  'SUPPORT.noStillNeedHelp': SUPPORT.en.noStillNeedHelp,
  'SUPPORT.noteForTeam': SUPPORT.en.noteForTeam,
  'SUPPORT.notYet': SUPPORT.en.notYet,
  'SUPPORT.reopenThread': SUPPORT.en.reopenThread,
  'SUPPORT.reply': SUPPORT.en.reply,
  'SUPPORT.replyAndClose': SUPPORT.en.replyAndClose,
  'SUPPORT.turn.answered': SUPPORT.en.turn.answered,
  'SUPPORT.turn.asker': SUPPORT.en.turn.asker,
  'SUPPORT.turn.closed': SUPPORT.en.turn.closed,
  'SUPPORT.turn.holder': SUPPORT.en.turn.holder,
  'SUPPORT.yesSolved': SUPPORT.en.yesSolved,
  'SUPPORT.yesThatsIt': SUPPORT.en.yesThatsIt,
  'TERMS.agreeAndContinue': TERMS.en.agreeAndContinue,
  'TERMS.join': TERMS.en.join,
  'TERMS.starWaiting': TERMS.en.starWaiting,
  'TOUR.back': TOUR.en.back,
  'TOUR.done': TOUR.en.done,
  'TOUR.next': TOUR.en.next,
  'TOUR.skip': TOUR.en.skip,
  'WIKI.delete': WIKI.en.delete,
  'WIKI.edit': WIKI.en.edit,
  'WIKI.postToBoard': WIKI.en.postToBoard,
  'WIKI.remove': WIKI.en.remove,
  'WIKI.stillTrue': WIKI.en.stillTrue,
}

/**
 * THE es COLUMN (S5, HLP-47). A Spanish twin of an everyone guide quotes the
 * same keys, and they fill from the screens' own `es` copy here. Only keys an
 * everyone guide uses need a row; the suite fails when a key a `.es-CL.md`
 * file uses is missing from this column, so a Spanish guide never quotes an
 * English button the Spanish screen does not show.
 */
export const GUIDE_COPY_ES: Record<string, Quote> = {
  'SHOP.waiting': SHOP.es.waiting,
  'SHOP.readyToCollect': SHOP.es.readyToCollect,
  'SHOP.collected': SHOP.es.collected,
  'SHOP.cancelled': SHOP.es.cancelled,
  'SHOP.yourOrders': SHOP.es.yourOrders,
  'ASK.ask': ASK.es.ask,
  'ASK.attachPicture': ASK.es.attachPicture,
  'ASK.capturePage': ASK.es.capturePage,
  'ASK.helpLead': ASK.es.helpLead,
  'ASK.helpLink': ASK.es.helpLink,
  'ASK.inferred': ASK.es.inferred,
  'ASK.lookedAt': ASK.es.lookedAt,
  'ASK.removePicture': ASK.es.removePicture,
  'ASK.requestLink.idea': ASK.es.requestLink.idea,
  'ASK.requestLinkNote': ASK.es.requestLinkNote,
  'ASK.startersHead': ASK.es.startersHead,
  'ASK.startOver': ASK.es.startOver,
  'ASK.wasUseful': ASK.es.wasUseful,
  'ASK.whatWasWrong': ASK.es.whatWasWrong,
  'CARD.editCard': CARD.es.editCard,
  'CARD.howItWorks': CARD.es.howItWorks,
  'CARD.saveContact': CARD.es.saveContact,
  'CARD.saveImage': CARD.es.saveImage,
  'CARD.share': CARD.es.share,
  'CARD.show': CARD.es.show,
  'COMPOSE.addPhotos': COMPOSE.es.addPhotos,
  'COMPOSE.checkedNote': COMPOSE.es.checkedNote,
  'COMPOSE.optOutLink': COMPOSE.es.optOutLink,
  'COMPOSE.post': COMPOSE.es.post,
  'COMPOSE.postAsLabel': COMPOSE.es.postAsLabel,
  'COMPOSE.saveAsDraft': COMPOSE.es.saveAsDraft,
  'COMPOSE.takeDown': COMPOSE.es.takeDown,
  'CONTESTS.takeBack': CONTESTS.es.takeBack,
  'CONTESTS.voteFor': CONTESTS.es.voteFor,
  'EMAILS.head': EMAILS.es.head,
  'EMAILS.lede': EMAILS.es.lede,
  'GUIDES.askAbout': GUIDES.es.askAbout,
  'HELP.guidesOpen': HELP.es.guidesOpen,
  'HELP.kindAll': HELP.es.kindAll,
  'HELP.kindFeedback': HELP.es.kindFeedback,
  'HELP.kindQuestion': HELP.es.kindQuestion,
  'HELP.newAskOne': HELP.es.newAskOne,
  'HELP.newFeedback': HELP.es.newFeedback,
  'HELP.newLabel': HELP.es.newLabel,
  'HELP.newTicket': HELP.es.newTicket,
  'HELP.priorityLabel': HELP.es.priorityLabel,
  'HELP.priority.cant_work': HELP.es.priorities.cant_work.label,
  'HELP.priority.blocked': HELP.es.priorities.blocked.label,
  'HELP.priority.annoying': HELP.es.priorities.annoying.label,
  'HELP.priority.question': HELP.es.priorities.question.label,
  'HELP.sendFeedback': HELP.es.sendFeedback,
  'HELP.ticketsTitle': HELP.es.ticketsTitle,
  'HELP.whoReads': HELP.es.whoReads,
  'HELP.wikiBody': HELP.es.wikiBody,
  'HOME.editThisPost': HOME.es.editThisPost,
  'HOME.postedBy': (a) => HOME.es.postedBy(a),
  'HOME.postSomething': HOME.es.postSomething,
  'HOME.recognizeColleague': HOME.es.recognizeColleague,
  'HOME.showOriginal': HOME.es.showOriginal,
  'HOME.translatedByOne': HOME.es.translatedByOne,
  'HOME.videoChangeTime': HOME.es.videoChangeTime,
  'HOME.videoHiddenUntil': (a) => HOME.es.videoHiddenUntil(a),
  'HOME.videoHideUntil': HOME.es.videoHideUntil,
  'HOME.videoShowFrom': HOME.es.videoShowFrom,
  'HOME.videoShowNow': HOME.es.videoShowNow,
  'HOME.videoShownToAll': HOME.es.videoShownToAll,
  'HOME.videoViewsTitle': HOME.es.videoViewsTitle,
  'HOME.yourDrafts': HOME.es.yourDrafts,
  'LOCALE.heading': LANGUAGE_HEADING,
  'NOTICES_PAGE.manageHeading': NOTICES_PAGE.es.manageHeading,
  'NOTICES_PAGE.previewHeading': NOTICES_PAGE.es.previewHeading,
  'ONE_QUESTION.edit': ONE_QUESTION.es.edit,
  'ONE_QUESTION.submit': ONE_QUESTION.es.submit,
  'ONE_QUESTION.withdraw': ONE_QUESTION.es.withdraw,
  'PROFILE.languageHead': PROFILE.es.languageHead,
  'PROFILE.photoOnlyOnCard': PROFILE.es.photoOnlyOnCard,
  'PROFILE.readHead': PROFILE.es.readHead,
  'PROFILE.whatWeHold': PROFILE.es.whatWeHold,
  'SHELL.bellAll': SHELL.es.bellAll,
  'SHELL.bellNew': SHELL.es.bellNew,
  'SHELL.hubShort': SHELL.es.hubShort,
  'SHELL.markAllRead': SHELL.es.markAllRead,
  'SHELL.navAdmin': SHELL.es.nav.Admin,
  'SHELL.navHelp': SHELL.es.nav.Help,
  'SHELL.navPhotos': SHELL.es.nav.Photos,
  'SHELL.navSurveys': SHELL.es.nav.Surveys,
  'SHELL.signOut': SHELL.es.signOut,
  'SHELL.takeTour': SHELL.es.takeTour,
  'SHELL.terms': SHELL.es.terms,
  'SHELL.yourCard': SHELL.es.yourCard,
  'SHELL.yourProfile': SHELL.es.nav['Your profile'],
  'SIGNIN.emailMeACode': SIGN_IN_COPY.es.emailMeACode,
  'SIGNIN.inactiveTitle': SIGN_IN_COPY.es.inactiveTitle,
  'SIGNIN.notFoundEmail': SIGN_IN_ERRORS.es.notFoundEmail,
  'SIGNIN.sendAnother': SIGN_IN_COPY.es.sendAnother,
  'SIGNIN.supportAddress': SUPPORT_ADDRESS,
  'SIGNIN.usePhoneInstead': SIGN_IN_COPY.es.usePhoneInstead,
  'STARS.addStars': STARS.es.addStars,
  'STARS.give': STARS.es.give,
  'STARS.inYourWords': STARS.es.inYourWords,
  'STARS.recognizeColleague': STARS.es.recognizeColleague,
  'STARS.store': STARS.es.store,
  'STARS.wall': STARS.es.wall,
  'STARS.whatFor': STARS.es.whatFor,
  'STARS.who': STARS.es.who,
  'STARS.withYourName': STARS.es.withYourName,
  'SUPPORT.addNote': SUPPORT.es.addNote,
  'SUPPORT.closedAsk': SUPPORT.es.closedAsk,
  'SUPPORT.closeThread': SUPPORT.es.closeThread,
  'SUPPORT.details': SUPPORT.es.details,
  'SUPPORT.didItAnswer': SUPPORT.es.didItAnswer,
  'SUPPORT.handedToYou': SUPPORT.es.handedToYou,
  'SUPPORT.insertSavedReply': SUPPORT.es.insertSavedReply,
  'SUPPORT.noStillNeedHelp': SUPPORT.es.noStillNeedHelp,
  'SUPPORT.noteForTeam': SUPPORT.es.noteForTeam,
  'SUPPORT.notYet': SUPPORT.es.notYet,
  'SUPPORT.reopenThread': SUPPORT.es.reopenThread,
  'SUPPORT.reply': SUPPORT.es.reply,
  'SUPPORT.replyAndClose': SUPPORT.es.replyAndClose,
  'SUPPORT.turn.answered': SUPPORT.es.turn.answered,
  'SUPPORT.turn.asker': SUPPORT.es.turn.asker,
  'SUPPORT.turn.closed': SUPPORT.es.turn.closed,
  'SUPPORT.turn.holder': SUPPORT.es.turn.holder,
  'SUPPORT.yesSolved': SUPPORT.es.yesSolved,
  'SUPPORT.yesThatsIt': SUPPORT.es.yesThatsIt,
  'TERMS.agreeAndContinue': TERMS.es.agreeAndContinue,
  'TERMS.join': TERMS.es.join,
  'TERMS.starWaiting': TERMS.es.starWaiting,
  'TOUR.back': TOUR.es.back,
  'TOUR.done': TOUR.es.done,
  'TOUR.next': TOUR.es.next,
  'TOUR.skip': TOUR.es.skip,
  'WIKI.delete': WIKI.es.delete,
  'WIKI.edit': WIKI.es.edit,
  'WIKI.postToBoard': WIKI.es.postToBoard,
  'WIKI.remove': WIKI.es.remove,
  'WIKI.stillTrue': WIKI.es.stillTrue,
}

/**
 * What a `::widget` IS, in one plain line - for Ask One, which reads a guide
 * as text and cannot draw the interactive part (the `guides` tool). One line
 * per widget in GUIDE_WIDGETS; the suite checks they match.
 */
export const GUIDE_WIDGET_TEXT: Record<string, { en: string; es: string }> = {
  'order-flow': {
    en: '(On the page: a diagram of an order going from Waiting to Ready to collect to Collected, or Cancelled.)',
    es: '(En la página: un diagrama de un pedido que pasa de En espera a Listo para retirar a Retirado, o Cancelado.)',
  },
  'practice-shelf': {
    en: '(On the page: a practice shelf to try counting, saving and handing over without touching real stock.)',
    es: '(En la página: un estante de práctica para contar, guardar y entregar sin tocar el stock real.)',
  },
  'scan-screens': {
    en: '(On the page: the screens a manager sees after scanning a pickup code.)',
    es: '(En la página: las pantallas que ve un encargado al escanear un código de retiro.)',
  },
  'item-kinds': {
    en: '(On the page: the four kinds of item - shelf stock only, limited window, waiting list, pre-order - from two settings.)',
    es: '(En la página: los cuatro tipos de artículo - solo stock, ventana limitada, lista de espera, preventa - a partir de dos ajustes.)',
  },
}

const isEs = (locale: string | null | undefined) => !!locale && (locale === 'es' || locale.startsWith('es-'))

/** Fill one key, in the language of the text it sits in. An unknown key
 *  renders as itself, visibly - the suite is what keeps that from ever
 *  shipping. A Spanish section fills from the es column; a section shown in
 *  English (113's fallback) fills in English. */
export function fillGuideCopy(key: string, arg: string | null, locale: string = 'en-CA'): string {
  const q = (isEs(locale) ? GUIDE_COPY_ES[key] : undefined) ?? GUIDE_COPY[key]
  if (q === undefined) return `{{${key}}}`
  return typeof q === 'function' ? q(arg ?? '') : q
}

export function isGuideCopyKey(key: string, locale: string = 'en-CA'): boolean {
  return isEs(locale) ? key in GUIDE_COPY_ES : key in GUIDE_COPY
}

/** The argument placeholder in the snapshot: a quote that takes an argument
 *  is written once with this where the argument goes. */
export const GUIDE_COPY_ARG = '\u0000ARG\u0000'

/**
 * Every quote and widget line as plain strings - what
 * `supabase/scripts/guides_to_sql.mts` writes into
 * `supabase/functions/ask-one/guideCopy.generated.ts`, because the Edge
 * Function cannot import web/src. The web suite fails when that file differs
 * from this, so rewording a button without regenerating goes red.
 */
export function guideCopySnapshot(): {
  en: Record<string, string>
  es: Record<string, string>
  widgets: Record<string, { en: string; es: string }>
} {
  const flat = (r: Record<string, Quote>) =>
    Object.fromEntries(
      Object.keys(r)
        .sort()
        .map((k) => {
          const q = r[k]
          return [k, typeof q === 'function' ? q(GUIDE_COPY_ARG) : q]
        }),
    )
  const widgets = Object.fromEntries(Object.keys(GUIDE_WIDGET_TEXT).sort().map((k) => [k, GUIDE_WIDGET_TEXT[k]]))
  return { en: flat(GUIDE_COPY), es: flat(GUIDE_COPY_ES), widgets }
}
